import React, { useCallback, useEffect, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Tooltip } from 'flowbite-react';
import { useAuth } from '../../../functions/useAuth';
import { NavLink } from 'react-router-dom';
import { useUser } from '../../../components/UserContext';
import { useVersion } from '../../../contexts/VersionContext';
import NotificationBell from '../../../components/NotificationBell';
import unknownUser from '../../../assets/unknownUser.jpg';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../contexts/ThemeContext';
  
const UserMenu: React.FC = () => {
  const { logout, user } = useUser();
  const { connectionStatus, currentVersion, latestVersion, openChangelog } = useVersion();
  const { theme, toggleTheme } = useTheme();
  const [ascundeButoane, setAscundeButoane] = useState(false);

  useEffect(() => {
    if(localStorage.getItem('ascundeButoane')){
      setAscundeButoane(true);
    }
  }, []);

  useEffect(() => {
    if(ascundeButoane){
      localStorage.setItem('ascundeButoane', 'true');
    }else{
      localStorage.removeItem('ascundeButoane');
    }
  }, [ascundeButoane]);

  const stergeCacheTabele = useCallback(() => {
    Object.keys(localStorage)
      .filter(key => key.startsWith('agGrid-'))
      .forEach(key => localStorage.removeItem(key));
  }, []);

  const getTooltipContent = () => {
    switch (connectionStatus) {
      case 'offline':
        return 'Lipsă internet';
      case 'unauthorized':
        return 'Neautentificat (Solicită Refresh sau login dintr-un nou tab)';
      case 'outdated':
        return `Versiune nouă disponibilă (ești pe v${currentVersion}, disponibil v${latestVersion}) — click pentru noutăți`;
      case 'online':
        return `Aplicația este actualizată (v${currentVersion}) — click pentru noutăți`;
      default:
        return 'Status necunoscut';
    }
  };

  const getDotColor = () => {
    switch (connectionStatus) {
      case 'offline':
        return 'bg-red-500';
      case 'unauthorized':
        return 'bg-red-500';
      case 'outdated':
        return 'bg-orange-600';
      case 'online':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Tooltip
        content={getTooltipContent()}
        placement="bottom"
      >
        {/* Bulina de status e și intrarea către istoricul de versiuni. */}
        <button
          type="button"
          onClick={openChangelog}
          aria-label="Vezi noutățile versiunilor"
          className="relative flex items-center rounded-full p-1 transition-transform hover:scale-125"
        >
          <div
            className={`w-3 h-3 rounded-full ${getDotColor()}`}
          />
        </button>
      </Tooltip>

      <button onClick={toggleTheme} className="p-1 rounded-md text-gray-500 dark:text-dm-text-muted hover:bg-gray-100 dark:hover:bg-dm-surface">
        {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
      </button>

      <NotificationBell />

      <Menu as="div" className="relative inline-block text-left">
        <div>
          <MenuButton>
              <a href="#" className="group block flex-shrink-0">
                  <div className="flex items-center">
                      <div>
                        <img alt={user?.name} src={unknownUser} className="inline-block h-7 w-7 rounded-full"/>
                      </div>
                      <div className="ml-3">
                          <p className="text-xs font-semibold text-gray-700 dark:text-dm-text group-hover:text-gray-900 dark:group-hover:text-dm-text text-left">{ user?.name }</p>
                          <p className="text-xs font-medium text-gray-500 dark:text-dm-text-muted group-hover:text-gray-700 dark:group-hover:text-dm-text-secondary text-left">{ user?.email }</p>
                      </div>
                  </div>
              </a>
          </MenuButton>
        </div>

      <MenuItems
        transition
        className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-dm-border rounded-md bg-white dark:bg-dm-surface shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-dm-border transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="px-4 py-3">
          <p className="text-sm dark:text-dm-text-muted">Ești conectat cu</p>
          <p className="truncate text-sm font-medium text-gray-900 dark:text-dm-text">{ user?.email }</p>
        </div>
        <div className="py-1">
          <MenuItem>
            <NavLink to="/settings/profile" className="block px-4 py-2 text-sm text-gray-700 dark:text-dm-text data-[focus]:bg-gray-100 dark:data-[focus]:bg-dm-hover data-[focus]:text-gray-900 dark:data-[focus]:text-dm-text">Setări profil</NavLink>
          </MenuItem>
          <MenuItem>
            <a
              onClick={() => setAscundeButoane(!ascundeButoane)}
              className="block px-4 py-2 text-sm text-gray-700 dark:text-dm-text data-[focus]:bg-gray-100 dark:data-[focus]:bg-dm-hover data-[focus]:text-gray-900 dark:data-[focus]:text-dm-text cursor-pointer"
            >
              { ascundeButoane ? 'Arata butoane' : 'Ascunde butoane' }
            </a>
          </MenuItem>
          <MenuItem>
            <a
              onClick={stergeCacheTabele}
              className="block px-4 py-2 text-sm text-gray-700 dark:text-dm-text data-[focus]:bg-gray-100 dark:data-[focus]:bg-dm-hover data-[focus]:text-gray-900 dark:data-[focus]:text-dm-text cursor-pointer"
            >
              Sterge cache tabele
            </a>
          </MenuItem>
        </div>
        <div className="py-1">
            <MenuItem>
              <button onClick={logout} type="button" className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dm-text data-[focus]:bg-gray-100 dark:data-[focus]:bg-dm-hover data-[focus]:text-gray-900 dark:data-[focus]:text-dm-text" >
                Log out
              </button>
            </MenuItem>
        </div>
      </MenuItems>
      </Menu>
    </div>
  )
}

export default UserMenu;